import { request } from '../../../../sharedLegacy/utils/api'
import {
  HTTPMethod,
  type APIResponse,
} from '../../../../sharedLegacy/utils/api.types'

export type PollDto = {
  /** ID of the Poll */
  poll_id: number
  /** ID of the object of which the poll belongs */
  object_id: number
  /** Type of the object of which the poll belongs */
  object_type: 'live' // only live supported atm.
  /** The poll question */
  question: string
  /* Date string of when the poll was opened. */
  opened_date: string
  /** Numbers of options related to the question of the poll */
  options_count: number
  /** Is the poll open/started */
  open_p: boolean
  /** Options related to the question of the poll */
  options: Array<{
    /** ID of the poll option */
    poll_option_id: number
    /** The poll option */
    option: string
    /** Numbers of answers related to the poll option */
    answer_count: number
    /** Percentage of answers related to the poll option */
    answer_percentage: number
    /** Formatted answers percentage */
    answer_percentage_fmt: string
  }>
}

type UpdateRequest = {
  /** ID of the poll to be updated */
  poll_id: number
  /** Question for the poll */
  question?: string
  /** Open or close the poll for answering */
  open_p?: boolean
  /** Should result be displayed publicly on the object */
  display_results_p?: boolean
}

type UpdatePollDto = {
  /** ID of the poll that was updated */
  poll_id: string
}

const pollEndpoint = () => ({
  list: (id: number) => {
    const params = new URLSearchParams({ object_id: `${id}` })
    return `/api/2/poll/list?${params}`
  },
  update: () => '/api/2/poll/update',
})

/**
 * Get all polls for an object (webinar)
 *
 * @param {string} objId
 */
async function getPolls(objId: number): Promise<PollDto[]> {
  const { data } = await request<{}, APIResponse<PollDto[]>>({
    method: HTTPMethod.GET,
    url: pollEndpoint().list(objId),
  })
  return data
}

/**
 * Start a specific poll
 *
 * @param {number} pollId ID of the poll
 * @returns {Promise} DTO containing the ID of the updated poll
 */
async function startPoll(pollId: number): Promise<UpdatePollDto> {
  const { data } = await request<UpdateRequest, APIResponse<UpdatePollDto>>({
    method: HTTPMethod.POST,
    url: pollEndpoint().update(),
    data: {
      poll_id: pollId,
      open_p: true,
    },
  })
  return data
}

/**
 * End a specific poll
 *
 * @param {number} pollId ID of the poll
 * @returns {Promise} DTO containing the ID of the updated poll
 */
async function endPoll(pollId: number): Promise<UpdatePollDto> {
  const { data } = await request<UpdateRequest, APIResponse<UpdatePollDto>>({
    method: HTTPMethod.POST,
    url: pollEndpoint().update(),
    data: {
      poll_id: pollId,
      open_p: false,
    },
  })
  return data
}

export { getPolls, startPoll, endPoll }
